import { rd } from '@passionware/monads';
import { useHotkeys } from 'react-hotkeys-hook';
import { get } from 'lodash';
import { featureFlags, AtellioFeatureGroupDefinition } from '../../features';
import { SHELL_FEATURE_FLAGS } from 'v5/core/app-scope/shellFeatureFlags';
import { WithAccessService } from '../../services/front/AccessService/AccessService';
import { WithAppScopeService } from '../../services/internal/AppScopeService/AppScopeService';
import { WithServices } from '../../platform/ts/services';
import { WithFeatureManagementService } from '../../services/front/FeatureManagementService/FeatureManagementService';
import { WithFeatureService } from '../../services/front/FeatureService/FeatureService';
import { FeatureName } from '../../platform/feature-management/feature-management';

export interface FeatureShortcutsWidgetProps
  extends WithServices<
    [
      WithFeatureManagementService,
      WithFeatureService,
      WithAppScopeService,
      WithAccessService
    ]
  > {}

/**
 * Component that registers keyboard shortcuts for feature flags.
 * Only works if user has permission to manage feature flags.
 */
export function FeatureShortcutsWidget(props: FeatureShortcutsWidgetProps) {
  const {
    featureManagementService,
    featureService,
    appScopeService,
    accessService
  } = props.services;
  const can = accessService.useCan(
    appScopeService.useOrganizationScopeIdentity()
  );
  const canManageFeatureFlags = rd.tryGet(
    can({ action: 'manage_feature_flags' })
  );

  const browsingScope = appScopeService.useResolvedScopeIdentity();
  const organizationScope = appScopeService.useOrganizationScopeIdentity();

  // Collect all features with shortcuts
  const featuresWithShortcuts = featureFlags.groups.flatMap(group =>
    group.entries.filter(entry => 'shortcut' in entry && entry.shortcut)
  );

  // Register hotkeys for each feature with a shortcut
  // fixed array - safe to call hooks in a loop
  featuresWithShortcuts.forEach(entry => {
    const featureId = entry.id as FeatureName<AtellioFeatureGroupDefinition>;
    const scope = SHELL_FEATURE_FLAGS.has(featureId)
      ? organizationScope
      : browsingScope;
    const featureRd = featureService.useFeature(scope, featureId);

    useHotkeys(
      get(entry, 'shortcut', ''),
      event => {
        event.preventDefault();

        if (!canManageFeatureFlags) {
          return;
        }

        const currentValue = rd.tryGet(featureRd);
        if (currentValue === undefined || currentValue === null) {
          return;
        }

        let newValue: boolean | string;

        if (entry.flagSpec.type === 'boolean') {
          newValue = !currentValue;
        } else {
          const options = entry.flagSpec.options;
          const currentIndex = options.indexOf(
            currentValue as (typeof options)[number]
          );
          const nextIndex = (currentIndex + 1) % options.length;
          newValue = options[nextIndex] as string;
        }

        featureManagementService.scheduleAction({
          type: 'setFeatureFlag',
          source: 'frontend',
          featureId,
          value: newValue
        });

        void featureManagementService.saveConfig();
      },
      {
        enableOnFormTags: true,
        enabled: canManageFeatureFlags
      },
      [canManageFeatureFlags, featureRd]
    );
  });

  return null;
}
